import { useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Download } from "lucide-react";

const monthsFrom = (offset = 0) => {
    const d = new Date();
    d.setMonth(d.getMonth() - offset);
    return d.toISOString().slice(0, 7);
};

const monthLabel = (ym) => {
    if (!ym) return "";
    const d = new Date(ym + "-01");
    return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
};

export default function GstReports() {
    const [month, setMonth] = useState(monthsFrom(0));
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [mode, setMode] = useState("month"); // month | range

    const buildUrl = () => {
        const p = new URLSearchParams();
        if (mode === "month" && month) p.set("month", month);
        if (mode === "range") {
            if (start) p.set("start", start);
            if (end) p.set("end", end);
        }
        return `${API}/reports/gstr1.xlsx?${p.toString()}`;
    };

    const download = async () => {
        try {
            const url = buildUrl();
            const r = await api.get(url.replace(API, ""), { responseType: "blob" });
            const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `gstr1-${mode === "month" ? month : "range"}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success("GSTR-1 export downloaded");
        } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    };

    return (
        <div data-testid="gstr1-page">
            <div className="mb-6">
                <h1 className="text-3xl hdb-heading font-black text-slate-900">GSTR-1 Report</h1>
                <p className="text-slate-500 mt-1 text-sm">
                    Downloads a single Excel with one tab per section: B2B · B2CL · B2CS · CDNR · HSN Summary · Docs Issued
                </p>
            </div>

            <div className="hdb-card p-6 mb-4">
                <div className="flex gap-2 mb-6">
                    {["month", "range"].map((m) => (
                        <button key={m} onClick={() => setMode(m)}
                                className={`px-4 py-2 rounded-md text-xs font-semibold border ${
                                    mode === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"
                                }`}
                                data-testid={`mode-${m}`}>
                            {m === "month" ? "By month" : "Custom date range"}
                        </button>
                    ))}
                </div>

                {mode === "month" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                        <div>
                            <label className="hdb-label">Filing month</label>
                            <input type="month" className="hdb-input hdb-mono" value={month}
                                   onChange={(e) => setMonth(e.target.value)}
                                   data-testid="month-picker" />
                            <div className="text-[11px] text-slate-500 mt-1">
                                {month ? `Will export invoices dated in ${monthLabel(month)}.` : "Pick a month above."}
                            </div>
                        </div>
                        <div>
                            <button onClick={download} className="hdb-btn hdb-btn-primary"
                                    data-testid="download-gstr1-btn">
                                <FileSpreadsheet size={14} /> Download GSTR-1 Excel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label className="hdb-label">From</label>
                            <input type="date" className="hdb-input hdb-mono" value={start}
                                   onChange={(e) => setStart(e.target.value)} />
                        </div>
                        <div>
                            <label className="hdb-label">To</label>
                            <input type="date" className="hdb-input hdb-mono" value={end}
                                   onChange={(e) => setEnd(e.target.value)} />
                        </div>
                        <button onClick={download} className="hdb-btn hdb-btn-primary"
                                data-testid="download-gstr1-btn">
                            <Download size={14} /> Download
                        </button>
                    </div>
                )}
            </div>

            {/* Quick month presets */}
            <div className="hdb-card p-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Quick pick</div>
                <div className="flex flex-wrap gap-2">
                    {[0, 1, 2, 3].map((off) => {
                        const m = monthsFrom(off);
                        return (
                            <button key={m} onClick={() => { setMode("month"); setMonth(m); }}
                                    className="hdb-btn hdb-btn-ghost text-xs"
                                    data-testid={`quick-month-${off}`}>
                                {monthLabel(m)}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
